/**
 * One expense report at the desk: the items as a table, the category summary
 * and exports beside it, and the item being edited in a side panel.
 *
 * Receipts can be dropped straight on the page. Dropped on a row, they attach
 * to that expense; dropped anywhere else, each file becomes a draft expense —
 * dated today, named after the file, amount left for you to fill in. That is
 * the desk's answer to the phone's camera: download a month of PDF receipts,
 * drop them in, then work down the list.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { card } from "@/components/atoms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CategoryIcon, categoryIconKey } from "@/components/ExpenseIcons";
import { useChrome } from "@/context/ChromeContext";
import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import {
  downloadCsv, pbError, useExpenseCategories, useExpenseMutations, useExpenseReports, useExpenses, useReportMutations,
} from "@/hooks/useExpenses";
import { useClients } from "@/hooks/useTime";
import {
  categoryNamer, copyOfExpense, dayOf, fileStem, fmtDay, fmtInstant, fmtPeriod, isOnHold, needsAmount, isInvoiced, money, quickenCsvRows, reimbursementCsvRows,
  sortCategories, todayIso, toCsv, totalsFrom,
} from "@/lib/expenses";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { Expense } from "@/lib/types";

import { ExpenseEditor } from "./ExpenseEditor";
import { Package } from "./Package";
import { ReportForm } from "./ReportForm";
import { Flag, acceptableReceipts, ghostBtn, input, primaryBtn, td, th, useDropZone } from "./ui";

type Editing = { expense?: Expense; copyOf?: Expense; mileage?: boolean } | null;

export function ReportDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const toast = useToast();

  const { data: reports = [], isLoading } = useExpenseReports();
  const { data: allExpenses = [] } = useExpenses();
  const { data: cats = [] } = useExpenseCategories();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { saveExpense, setFlag } = useExpenseMutations();
  async function toggleFlag(e: Expense, field: "added_to_quicken" | "added_to_invoice") {
    const value = !e[field];
    try {
      await setFlag.mutateAsync({ ids: [e.id], field, value });
      toast(field === "added_to_quicken"
        ? (value ? "Marked recorded in Quicken" : "Unmarked in Quicken")
        : (value ? "Marked on an invoice" : "Unmarked from invoice"));
    } catch (err) {
      toast("Couldn't update: " + pbError(err));
    }
  }
  const { saveReport } = useReportMutations();

  const report = reports.find((r) => r.id === id);
  const client = clients.find((c) => c.id === report?.client);
  const project = projects.find((p) => p.id === report?.project);
  const catName = useMemo(() => categoryNamer(cats), [cats]);
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const mine = useMemo(() => allExpenses.filter((e) => e.report === id), [allExpenses, id]);
  const totals = useMemo(() => totalsFrom(mine, catName), [mine, catName]);

  const [editing, setEditing] = useState<Editing>(null);
  const [editingReport, setEditingReport] = useState(false);
  const [showPackage, setShowPackage] = useState(false);
  const [confirmInvoice, setConfirmInvoice] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(0);
  const [overRow, setOverRow] = useState(false);

  const invoiced = isInvoiced(report?.status);

  useChrome({
    title: report?.name ?? "Expense report",
    subtitle: report ? [client?.name, project?.name, fmtPeriod(report.start_date, report.end_date)].filter(Boolean).join(" · ") : "",
    back: { to: "/expenses", label: "Expenses" },
    actions: report ? (
      <>
        <button onClick={() => setEditingReport(true)} style={ghostBtn}>Edit report</button>
        <button onClick={() => setShowPackage(true)} style={primaryBtn}>Package</button>
      </>
    ) : null,
  }, [report, client, project]);

  /** Files dropped on the page: one draft expense each. */
  async function draftsFrom(files: File[]) {
    const ok = acceptableReceipts(files, toast);
    const category = sortCategories(cats).find((c) => !c.is_mileage && !c.archived)?.id ?? "";
    if (!ok.length) return;
    setBusy((n) => n + ok.length);
    let made = 0;
    for (const f of ok) {
      try {
        await saveExpense.mutateAsync({
          data: {
            report: id, date: todayIso(), category, amount: 0, is_mileage: false, reimbursable: true,
            description: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim(),
            receipt_captured: true,
          },
          add: [f],
        });
        made++;
      } catch (e) {
        toast(`${f.name}: ${pbError(e)}`);
      } finally {
        setBusy((n) => n - 1);
      }
    }
    if (made) toast(`Added ${made} draft expense${made === 1 ? "" : "s"} — fill in the amounts`);
  }

  /** Files dropped on a row: attach to that expense. */
  async function attach(e: Expense, files: File[]) {
    const ok = acceptableReceipts(files, toast);
    if (!ok.length) return;
    setBusy((n) => n + 1);
    try {
      await saveExpense.mutateAsync({ id: e.id, data: { receipt_captured: true }, add: ok });
      toast(`Attached ${ok.length} receipt${ok.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast("Couldn't attach: " + pbError(err));
    } finally {
      setBusy((n) => n - 1);
    }
  }

  // Off while a panel or dialog is up, so a drop that misses the panel's own
  // receipt box does not quietly create expenses behind it.
  const page = useDropZone((files) => { setOverRow(false); void draftsFrom(files); },
    !editing && !editingReport && !showPackage && !confirmInvoice);

  if (!report) {
    return (
      <div style={{ padding: "40px 36px", color: "var(--muted)", fontSize: 13 }}>
        {isLoading ? "Loading…" : "This report no longer exists."}
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown = totals.items
    .filter((e) => !needle || `${e.description ?? ""} ${catName(e.category)} ${e.notes ?? ""}`.toLowerCase().includes(needle))
    .sort((a, b) => dayOf(a.date).localeCompare(dayOf(b.date)));

  const csv = (kind: "reimbursement" | "quicken") => {
    const rows = kind === "quicken" ? quickenCsvRows(totals, catName) : reimbursementCsvRows(totals, catName);
    downloadCsv(toCsv(rows), `${fileStem(report.name)}_${kind}.csv`);
  };

  return (
    <div {...page.props} style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1200, position: "relative", minHeight: "100%" }}>
      {/* An outline, not a cover: the rows underneath stay visible, and are
          targets of their own. */}
      {page.active && (
        <div style={{
          position: "absolute", inset: 8, zIndex: 5, borderRadius: 14, pointerEvents: "none",
          border: "2px dashed var(--accent)", display: "flex", justifyContent: "center", alignItems: "flex-start",
        }}>
          <span style={{
            marginTop: -12, padding: "4px 12px", borderRadius: 999, fontSize: 12.5,
            background: "var(--accent)", color: "var(--onAccent)",
          }}>{overRow ? "Drop to attach to this expense" : "Drop to create one draft expense per file"}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ---- Items ---- */}
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <button onClick={() => setEditing({})} style={primaryBtn}>+ Expense</button>
            <button onClick={() => setEditing({ mileage: true })} style={ghostBtn}>+ Mileage</button>
            {busy > 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>Uploading {busy}…</span>}
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by description or category"
              style={{ ...input, marginTop: 0, width: 240, marginLeft: "auto" }} />
          </div>

          <div style={{ ...card, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Description</th>
                  <th style={th}>Category</th>
                  <th style={{ ...th, textAlign: "center" }} title="Receipt · Quicken · Invoice">R Q I</th>
                  <th style={{ ...th, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <ExpenseRow key={e.id} e={e} catLabel={catName(e.category)} iconKey={categoryIconKey(catById.get(e.category ?? "") ?? { name: catName(e.category) })}
                    onOpen={() => setEditing({ expense: e })} onHover={setOverRow}
                    onFlag={(field) => void toggleFlag(e, field)}
                    onDrop={(files) => { setOverRow(false); void attach(e, files); }} />
                ))}
                {shown.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, color: "var(--muted)", textAlign: "center", padding: 28 }}>
                    {needle ? "Nothing matches that." : "No expenses yet. Add one, or drop PDF receipts anywhere on this page."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted2)", marginTop: 8 }}>
            Drop a receipt on a row to attach it to that expense, or anywhere else on the page to start a new one.
          </div>
        </div>

        {/* ---- Summary ---- */}
        <div style={{ flex: "0 1 300px", minWidth: 260, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ ...card, padding: 18 }}>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>{totals.count} item{totals.count === 1 ? "" : "s"} · total reimbursable</div>
            <div className="mono" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", margin: "4px 0 12px" }}>{money(totals.total)}</div>
            {totals.held > 0 && (
              <div style={{ fontSize: 12.5, padding: "8px 10px", borderRadius: 8, background: "var(--tint)", color: "var(--ink3)", marginBottom: 8 }}>
                {totals.held} on hold · {money(totals.heldTotal)} not included yet
              </div>
            )}
            {totals.unpriced > 0 && (
              <div style={{ fontSize: 12.5, padding: "8px 10px", borderRadius: 8, background: "var(--warnTint)", color: "var(--warn)", marginBottom: 8 }}>
                {totals.unpriced} item{totals.unpriced === 1 ? "" : "s"} with no amount yet
              </div>
            )}
            {totals.missing > 0 && (
              <div style={{ fontSize: 12.5, padding: "8px 10px", borderRadius: 8, background: "var(--warnTint)", color: "var(--warn)", marginBottom: 12 }}>
                {totals.missing} receipt{totals.missing === 1 ? "" : "s"} still to capture
              </div>
            )}
            {Object.entries(totals.byCat).map(([c, v]) => (
              <div key={c} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderTop: "1px solid var(--tint2)", fontSize: 13 }}>
                <span style={{ color: "var(--ink3)", display: "inline-flex" }}>
                  <CategoryIcon name={categoryIconKey(cats.find((x) => x.name === c) ?? { name: c })} size={16} />
                </span>
                <span style={{ flex: 1 }}>{c}</span>
                <span className="mono">{money(v)}</span>
              </div>
            ))}
            {invoiced && report.invoiced_at && (
              <div style={{ fontSize: 12, color: "var(--good)", marginTop: 10 }}>
                Invoiced {fmtInstant(report.invoiced_at)}{report.invoice_number ? ` · ${report.invoice_number}` : ""}
              </div>
            )}
          </div>

          <div style={{ ...card, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => setShowPackage(true)} style={primaryBtn}>Reimbursement package</button>
            <button onClick={() => csv("reimbursement")} style={ghostBtn}>Export reimbursement CSV</button>
            <button onClick={() => csv("quicken")} style={ghostBtn}>Export for Quicken (CSV)</button>
            <button onClick={() => setConfirmInvoice(true)} style={ghostBtn}>{invoiced ? "Reopen report" : "Mark invoiced & archive"}</button>
          </div>
        </div>
      </div>

      {editing && (
        <ExpenseEditor
          key={editing.expense?.id ?? (editing.copyOf ? "copy:" + editing.copyOf.id : "new")}
          reportId={id} expense={editing.expense} copyOf={editing.copyOf} mileage={editing.mileage}
          onDuplicate={(e) => setEditing({ copyOf: copyOfExpense(e, id) })}
          onClose={() => setEditing(null)} />
      )}
      {editingReport && <ReportForm report={report} expenses={mine} onClose={() => setEditingReport(false)} />}
      {showPackage && (
        <Package report={report} client={client} project={project} totals={totals} cats={cats} onClose={() => setShowPackage(false)} />
      )}
      {confirmInvoice && (
        <ConfirmDialog
          title={invoiced ? "Reopen this report?" : "Mark invoiced and archive?"}
          message={invoiced ? "It moves back to Active." : "It moves to the Invoiced tab, here and on your phone. You can reopen it later."}
          confirmLabel={invoiced ? "Reopen" : "Mark invoiced"}
          danger={false}
          onConfirm={async () => {
            try {
              await saveReport.mutateAsync(invoiced
                ? { id, status: "active" }
                : { id, status: "archived", invoiced_at: new Date().toISOString() });
              toast(invoiced ? "Reopened" : "Marked invoiced");
              navigate(`/expenses?tab=${invoiced ? "active" : "invoiced"}`);
            } catch (e) {
              toast("Couldn't update: " + pbError(e));
            }
          }}
          onClose={() => setConfirmInvoice(false)}
        />
      )}
    </div>
  );
}

function ExpenseRow({ e, catLabel, iconKey, onOpen, onDrop, onHover, onFlag }: {
  e: Expense; catLabel: string; iconKey: string; onOpen: () => void; onDrop: (files: File[]) => void;
  onHover: (over: boolean) => void;
  onFlag: (field: "added_to_quicken" | "added_to_invoice") => void;
}) {
  const drop = useDropZone(onDrop);
  useEffect(() => { onHover(drop.active); }, [drop.active, onHover]);
  const draft = needsAmount(e);
  const sub = e.is_mileage
    ? `${e.miles || 0} mi × ${money(e.mileage_rate)}/mi${e.notes?.trim() ? ` · ${e.notes.trim()}` : ""}`
    : (e.receipts?.length ? `${e.receipts.length} receipt${e.receipts.length === 1 ? "" : "s"}` : "");
  return (
    <tr {...drop.props} onClick={onOpen} style={{
      cursor: "pointer", background: drop.active ? "var(--accentTint)" : undefined,
      outline: drop.active ? "2px dashed var(--accent)" : undefined, outlineOffset: -2,
    }}>
      <td style={{ ...td, whiteSpace: "nowrap", color: "var(--ink3)" }}>{fmtDay(e.date)}</td>
      <td style={td}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ color: "var(--ink3)", display: "inline-flex", flex: "none" }}><CategoryIcon name={iconKey} size={17} /></span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block" }}>{e.description || catLabel}</span>
            {sub && <span style={{ display: "block", fontSize: 11.5, color: "var(--muted)" }}>{sub}</span>}
          </span>
          {isOnHold(e) && (
            <span title="Not billed yet — left out of the total and the package" style={{
              fontSize: 10.5, fontWeight: 500, padding: "2px 7px", borderRadius: 999, flex: "none",
              background: "var(--tint2)", color: "var(--ink3)", textTransform: "uppercase", letterSpacing: "0.3px",
            }}>On hold</span>
          )}
        </div>
      </td>
      <td style={{ ...td, color: "var(--ink3)" }}>{catLabel}</td>
      <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-flex", gap: 4 }}>
          {e.is_mileage
            ? <span style={{ width: 18, display: "inline-block", color: "var(--muted3)" }}>–</span>
            : <Flag on={!!e.receipt_captured} title={e.receipt_captured ? "Receipt captured" : "Receipt missing"}>R</Flag>}
          <Flag on={!!e.added_to_quicken} title={e.added_to_quicken ? "In Quicken" : "Not in Quicken yet"}
            onToggle={() => onFlag("added_to_quicken")}>Q</Flag>
          <Flag on={!!e.added_to_invoice} title={e.added_to_invoice ? "On an invoice" : "Not invoiced yet"}
            onToggle={() => onFlag("added_to_invoice")}>I</Flag>
        </span>
      </td>
      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: isOnHold(e) ? "var(--muted2)" : undefined }} className="mono">
        {draft
          ? <span style={{ color: "var(--warn)" }} title="Saved without an amount — open it to finish">{e.is_mileage ? "needs miles" : "needs amount"}</span>
          : money(e.amount)}
      </td>
    </tr>
  );
}
